import { Caption } from '~/shared/Typography';
import { i18nFn, useSpringBigStore } from '@springbig/shared';
import React, { useState } from 'react';
import { Actionsheet, Pressable, SmallCloseIcon, VStack } from 'native-base';
import { useGeneralStyles } from '~/shared/styles';

type RestrictionsDisclaimerProps = {
    t: i18nFn,
    type: 'dollar' | 'point',
    maxOneTime: number,
    maxPeriod: number,
};

export const RestrictionsDisclaimer = ({
    t,
    type,
    maxOneTime,
    maxPeriod,
}: RestrictionsDisclaimerProps) => {

    const { member, merchant } = useSpringBigStore();
    const gStyle = useGeneralStyles();
    const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);

    return (
        <>
            { merchant?.gift_card_points_restrictions ?
                member?.remaining_point_amount_for_current_period === merchant.gc_point_amount_per_period &&
                <Caption opacity={.6} fontSize={12}>
                    { t('giftCard.restrictions.point_limit', [
                        merchant.gc_point_amount_one_time,
                        merchant.gc_point_amount_per_period
                    ])
                    }
                </Caption> : 
                <></>
            }
            {
                merchant?.gift_card_points_restrictions &&
                member &&
                member?.remaining_point_amount_for_current_period < merchant.gc_point_amount_one_time ?
                <Caption opacity={.6} fontSize={12}>
                    { t('giftCard.restrictions.one_time_point_limit_under', [
                        member?.remaining_point_amount_for_current_period,
                        merchant.gc_point_amount_per_period
                    ])
                    }
                </Caption> : 
                <></>
            }
            {
                merchant?.gift_card_points_restrictions &&
                member &&
                member?.remaining_point_amount_for_current_period !== merchant.gc_point_amount_per_period &&
                member?.remaining_point_amount_for_current_period > merchant.gc_point_amount_one_time ?
                <Caption opacity={.6} fontSize={12}>
                    { t('giftCard.restrictions.one_time_point_limit_over', [
                        merchant.gc_point_amount_one_time,
                        member?.remaining_point_amount_for_current_period,
                        merchant.gc_point_amount_per_period
                    ])
                    }
                </Caption> : 
                <></>
            }
            { merchant?.gift_card_restrictions &&
                Number(member?.remaining_dollar_amount_for_current_period) / 100 === Number(merchant.gc_dollar_amount_per_period) ?
                <Caption opacity={.6} fontSize={12}>
                    { t('giftCard.restrictions.cash_limit', [
                        merchant.gc_dollar_amount_one_time.split('.')[0],
                        merchant.gc_dollar_amount_per_period.split('.')[0]
                    ])
                    }
                </Caption> : 
                <></>
            }
            {
                merchant?.gift_card_restrictions &&
                Number(member?.remaining_dollar_amount_for_current_period) / 100 < Number(merchant.gc_dollar_amount_one_time) ?
                <Caption opacity={.6} fontSize={12}>
                    { t('giftCard.restrictions.one_time_dollar_limit_under', [
                        Number(member?.remaining_dollar_amount_for_current_period) / 100,
                        merchant.gc_dollar_amount_per_period.split('.')[0]
                    ])
                    }
                </Caption> : 
                <></>
            }
            {
                merchant?.gift_card_restrictions &&
                Number(member?.remaining_dollar_amount_for_current_period) / 100 !== Number(merchant.gc_dollar_amount_per_period) &&
                Number(member?.remaining_dollar_amount_for_current_period) / 100 > Number(merchant.gc_dollar_amount_one_time) ?
                <Caption opacity={.6} fontSize={12}>
                    { t('giftCard.restrictions.one_time_dollar_limit_over', [
                        merchant.gc_dollar_amount_one_time.split('.')[0],
                        Number(member?.remaining_dollar_amount_for_current_period) / 100,
                        merchant.gc_dollar_amount_per_period.split('.')[0]
                    ])
                    }
                </Caption> : 
                <></>
            }
            { (merchant?.gift_card_restrictions || (merchant?.gift_card_points_restrictions && member.balance > 0)) ?
                <Pressable onPress={() => setIsMoreInfoOpen(true)}>
                    <Caption fontSize={12} color={gStyle.link}>
                        { t('giftCard.more_info') }
                    </Caption>
                </Pressable> : 
                <></>
            }
            <Actionsheet isOpen={isMoreInfoOpen} onClose={() => setIsMoreInfoOpen(false)}>
                <Actionsheet.Content backgroundColor={gStyle.section_background} zIndex={9999} overflow='auto' w={'clamp(320px, 100vw, 480px)'} alignSelf='center'>
                    <Pressable onPress={() => setIsMoreInfoOpen(false)} position={'absolute'} top={4} right={4}>
                        <SmallCloseIcon size='md' color={gStyle.button_background} />
                    </Pressable>
                    <VStack space={4} p={4} w={'100%'}>
                        { merchant?.gift_card_restrictions &&
                            <VStack>
                                <Caption fontSize={18}>{ t('giftCard.restrictions.information.cash_title') }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.cash_one_time', [merchant?.gc_dollar_amount_one_time.split('.')[0] || '0']) }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.cash_per_period', [merchant?.gc_dollar_amount_per_period.split('.')[0] || '0']) }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.cash_period', [merchant?.gc_dollar_frequency_in_days || '0']) }</Caption>
                            </VStack>
                        }
                        { merchant?.gift_card_points_restrictions &&
                            <VStack>
                                <Caption fontSize={18}>{ t('giftCard.restrictions.information.points_title') }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.points_one_time', [Math.min(merchant?.gc_point_amount_one_time, maxOneTime) || '0']) }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.points_per_period', [Math.min(merchant?.gc_point_amount_per_period, maxPeriod) || '0']) }</Caption>
                                <Caption ml={4}>{ t('giftCard.restrictions.information.points_period', [merchant?.gc_point_frequency_in_days || '0']) }</Caption>
                            </VStack>
                        }
                    </VStack>
                </Actionsheet.Content>
            </Actionsheet>
        </>
    );
};
